// Per-line "Find cross-referenced part numbers" panel. When a part dead-ends on
// PartsBase, this resolves its NSN(s) via DLA FLIS (PUB LOG) and lists every
// other CAGE + reference number on those NSNs — re-queryable on PartsBase.
// Collapsed/on-demand; only offered while the line is being sourced.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Network, ChevronRight, Copy, Check } from 'lucide-react';
import { api, reportAltPartCopy, type AltPartsResponse, type LineStage } from '@/lib/api';
import {
  AltPartRelationshipLabel,
  AltPartCaveat,
  AltPartSourceLine,
} from './alt-part-provenance';

interface Props {
  rfqPublicId: string;
  lineItemPublicId: string;
  partNumber: string;
  stage: LineStage;
}

export function AltPartFinder({ rfqPublicId, lineItemPublicId, partNumber, stage }: Props) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['alt-parts', lineItemPublicId],
    enabled: open,
    queryFn: () =>
      api<AltPartsResponse>(
        `/api/rfqs/${rfqPublicId}/line-items/${lineItemPublicId}/alt-parts`,
        { tokenGetter: () => getToken() },
      ),
    staleTime: 5 * 60 * 1000,
  });

  const copyAll = async () => {
    if (!data?.altParts.length) return;
    const text = data.altParts.map((a) => a.refNumber).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      void reportAltPartCopy(partNumber, 'rfq_line', () => getToken());
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fires here too: navigator.clipboard is unavailable in a non-secure
      // context and on older Safari, and only counting the happy path would
      // bias the usage metric against exactly those sessions.
      void reportAltPartCopy(partNumber, 'rfq_line', () => getToken());
      window.prompt('Copy the cross-referenced P/Ns:', text);
    }
  };

  // On-demand only — and only while actively sourcing. Placed AFTER all hooks so
  // hook order stays stable if a line item's stage changes while mounted.
  if (stage !== 'received' && stage !== 'sourcing') return null;

  return (
    <div className="mt-3 border border-border rounded-md bg-surface-muted/40 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium hover:bg-surface-muted/70 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Network className="w-3.5 h-3.5 text-primary-strong" />
          <span className="text-primary-strong">Find cross-referenced part numbers</span>
          {open && data?.lookupStatus === 'found' && (
            <span className="pill pill-info">{data.altParts.length}</span>
          )}
        </span>
        <ChevronRight className={`w-3.5 h-3.5 text-foreground-muted transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 text-xs">
          {isLoading && <div className="text-foreground-muted py-2">Searching DLA FLIS…</div>}

          {isError && (
            <div className="text-destructive py-2">
              {(error as { message?: string })?.message ?? 'Lookup unavailable — try again later.'}
            </div>
          )}

          {data?.lookupStatus === 'not_found' && (
            <div className="text-foreground-muted py-2">
              No NSN match in DLA FLIS for <span className="font-mono">{partNumber}</span> (common for proprietary/commercial P/Ns).
            </div>
          )}

          {data?.lookupStatus === 'found' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 pt-0.5">
                <AltPartRelationshipLabel />
              </div>
              <div className="text-foreground-muted">
                NSN{data.nsns.length > 1 ? 's' : ''}:{' '}
                {data.nsns.map((n) => (
                  <span key={n.nsn} className="font-mono text-foreground mr-2">
                    {n.nsn}{n.itemName ? ` (${n.itemName})` : ''}
                  </span>
                ))}
              </div>

              {data.altParts.length === 0 ? (
                <div className="text-foreground-muted py-1">
                  This P/N is the only reference number on its NSN — nothing cross-referenced.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-muted">
                      {data.altParts.length} cross-referenced P/N{data.altParts.length === 1 ? '' : 's'}
                      {data.truncated ? ' (showing first 1000)' : ''} — re-query these on PartsBase:
                    </span>
                    <button onClick={copyAll} className="flex items-center gap-1 text-primary hover:underline">
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied' : 'Copy all P/Ns'}
                    </button>
                  </div>
                  <div className="overflow-x-auto border border-border rounded">
                    <table className="w-full min-w-[720px] text-left">
                      <thead className="bg-surface-muted/60 text-foreground-muted">
                        <tr>
                          <th className="px-2 py-1 font-medium">CAGE</th>
                          <th className="px-2 py-1 font-medium">Cross-Referenced P/N</th>
                          <th className="px-2 py-1 font-medium">Item Name</th>
                          <th className="px-2 py-1 font-medium">NSN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.altParts.map((a, i) => (
                          <tr key={`${a.cageCode}-${a.refNumber}-${i}`} className="border-t border-border">
                            <td className="px-2 py-1 font-mono">{a.cageCode}</td>
                            <td className="px-2 py-1 font-mono">{a.refNumber}</td>
                            <td className="px-2 py-1 text-foreground-muted">{a.itemName ?? '—'}</td>
                            <td className="px-2 py-1 font-mono text-foreground-muted">{a.nsn}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <AltPartCaveat className="pt-1 border-t border-border" />
              <AltPartSourceLine dataset={data.dataset} className="pt-0.5" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
